import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    InputAdornment,
    Link,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import { ChangeEvent, Fragment, ReactElement, useState } from "react"

import AppPaper from "../../components/surfaces/appPaper"
import Video from "../../components/video/video"
import DisplayIf from "../../components/wrappers/displayIf"
import { IQuestion, QUESTIONS } from "../../constants/faqs"
import { ENA_SYS_ADMIN, PUBLIC } from "../../constants/roles"
import { useGetUserData } from "../../hooks/useGetUserData"
import { SEARCH_BAR_ITEM_RADIUS_MULTIPLIER } from "../../themes/defaultThemeParams"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

/**
 * Displays a list of frequently asked questions in an accordion format with a search bar
 * and an option to add a video with instructions.
 */
const LearnMore = (): ReactElement => {
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))
    const { groups } = useGetUserData()
    const [searchTerm, setSearchTerm] = useState("")

    const categories: Record<string, IQuestion[]> = QUESTIONS.reduce<
        Record<string, IQuestion[]>
    >(
        (acc, question) => ({
            ...acc,
            [question.category]: [...(acc[question.category] || []), question],
        }),
        {}
    )

    const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(event.target.value)
    }

    const allFilteredQuestions = Object.keys(categories).flatMap((category) =>
        categories[category].filter(
            (question) =>
                question.question
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) &&
                (question.permittedRoles.includes(PUBLIC) ||
                    question.permittedRoles.some((role) =>
                        groups.includes(role)
                    ) ||
                    groups.includes(ENA_SYS_ADMIN))
        )
    )

    return (
        <Stack
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            height="100%"
            spacing={2}
        >
            <Typography
                variant={isSmallScreen ? "h4" : "h3"}
                color={theme.palette.text.primary}
                fontWeight={500}
            >
                {" "}
                Got a Question?{" "}
            </Typography>
            <Typography
                variant="h6"
                color={theme.palette.text.secondary}
                fontWeight={300}
                align="center"
            >
                Search our frequently asked questions for help, guidance and
                support on using ENA Connect Direct
            </Typography>
            <Stack width={{ xs: "100%", md: "56%" }}>
                <TextField
                    placeholder="What can we help you with?"
                    margin="normal"
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius:
                                SEARCH_BAR_ITEM_RADIUS_MULTIPLIER *
                                theme.shape.borderRadius,
                        },
                    }}
                />
            </Stack>
            <AppPaper
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                }}
            >
                <Stack height="100%" width={{ xs: "100%", md: "60%" }}>
                    {Object.keys(categories).map((category) => {
                        const filteredQuestionsForCategory =
                            allFilteredQuestions.filter(
                                (question) => question.category === category
                            )
                        if (filteredQuestionsForCategory.length === 0) {
                            return null
                        }
                        return (
                            <Fragment key={category}>
                                <Box
                                    paddingTop={3}
                                    paddingBottom={3}
                                    paddingLeft={2}
                                >
                                    <Typography
                                        variant="h5"
                                        fontWeight={600}
                                        color={theme.palette.text.secondary}
                                        paddingTop={5}
                                    >
                                        {category}
                                    </Typography>
                                </Box>
                                {filteredQuestionsForCategory.map(
                                    (question) => (
                                        <Accordion
                                            key={question.question}
                                            sx={{
                                                width: "100%",
                                                boxShadow: "none",
                                                background: "transparent",
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={
                                                    <KeyboardArrowDownIcon
                                                        style={{
                                                            fontSize: "40px",
                                                        }}
                                                    />
                                                }
                                            >
                                                <Typography
                                                    variant="h6"
                                                    paddingTop={1}
                                                >
                                                    {question.question}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography
                                                    paddingLeft={{
                                                        xs: 0,
                                                        sm: 3,
                                                    }}
                                                    component="div"
                                                    fontWeight={300}
                                                >
                                                    {question.answer}
                                                    <DisplayIf
                                                        condition={
                                                            !!question.videoId
                                                        }
                                                    >
                                                        <Video
                                                            videoUrl={
                                                                question.videoId
                                                            }
                                                        />
                                                    </DisplayIf>
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                )}
                            </Fragment>
                        )
                    })}

                    {allFilteredQuestions.length === 0 && searchTerm !== "" && (
                        <Box paddingTop={3} paddingBottom={3} paddingLeft={2}>
                            <Typography
                                variant="h5"
                                fontWeight={600}
                                color={theme.palette.text.secondary}
                                paddingTop={5}
                            >
                                No results found
                            </Typography>
                        </Box>
                    )}

                    <Stack paddingLeft={2} paddingTop={5} marginTop="auto">
                        <Box display="flex">
                            <Typography variant="body1" fontWeight={300}>
                                Still need help? Please raise a ticket with our
                                support team via the{" "}
                                <Link
                                    href={CONFIG.SN_PORTAL_URL}
                                    target="_blank"
                                >
                                    Connect Direct Support Portal
                                </Link>
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </AppPaper>
        </Stack>
    )
}

export default LearnMore
